import { useState, useEffect, useRef } from '@wordpress/element';
import apiFetch                        from '@wordpress/api-fetch';
import ProductCard     from '../../product-grid/components/ProductCard';
import SkeletonLoader  from '../../product-grid/components/SkeletonLoader';
import SliderTrack     from './SliderTrack';

function resolveSource( productSource, context ) {
	if ( productSource !== 'auto' ) return productSource;
	if ( context?.pageType === 'single_product' ) return 'related';
	if ( context?.pageType === 'product_category' ) return 'category_context';
	return 'recent';
}

function buildQuery( attributes, context ) {
	const { sourceCategory, defaultOrderby } = attributes;
	// `limit` here is already the effective limit (columns × rows for grid, raw limit for slider).
	const limit  = attributes.limit;
	const source = resolveSource( attributes.productSource, context );
	const params = new URLSearchParams();

	switch ( source ) {
		case 'related':
			if ( context?.relatedIds?.length ) {
				params.set( 'product_ids', context.relatedIds.join( ',' ) );
				params.set( 'per_page', String( context.relatedIds.length ) );
			} else {
				// No related IDs available (e.g. editor preview) — fall back to recent.
				params.set( 'per_page', String( limit ) );
				params.set( 'orderby', 'date' );
			}
			break;

		case 'category_context':
			params.set( 'per_page', String( limit ) );
			if ( context?.termSlug ) params.set( 'category', context.termSlug );
			if ( defaultOrderby ) params.set( 'orderby', defaultOrderby );
			break;

		case 'recent':
			params.set( 'per_page', String( limit ) );
			params.set( 'orderby', defaultOrderby && defaultOrderby !== 'menu_order' ? defaultOrderby : 'date' );
			break;

		case 'sale':
			params.set( 'per_page', String( limit ) );
			params.set( 'on_sale', '1' );
			if ( defaultOrderby ) params.set( 'orderby', defaultOrderby );
			break;

		case 'hand_picked':
			if ( attributes.productIds?.length ) {
				params.set( 'product_ids', attributes.productIds.join( ',' ) );
				params.set( 'per_page', String( attributes.productIds.length ) );
			} else {
				params.set( 'per_page', String( limit ) );
			}
			break;

		case 'category':
			params.set( 'per_page', String( limit ) );
			if ( sourceCategory ) params.set( 'category', sourceCategory );
			if ( defaultOrderby ) params.set( 'orderby', defaultOrderby );
			break;

		default:
			params.set( 'per_page', String( limit ) );
			if ( defaultOrderby ) params.set( 'orderby', defaultOrderby );
	}

	return params.toString();
}

export default function ProductShowcase( { attributes, isEditor } ) {
	const {
		displayMode,
		limit,
		columnsDesktop,
		columnsTablet,
		columnsMobile,
		gap,
	} = attributes;

	const context = attributes._context || {};

	// limit is the single source of truth — already validated in the editor
	// to be >= gridRows (at least 1 product per row).
	const effectiveLimit = limit;

	const [ products, setProducts ] = useState( [] );
	const [ loading, setLoading ]   = useState( true );
	const abortRef                  = useRef( null );

	useEffect( () => {
		if ( abortRef.current ) abortRef.current.abort();
		const controller = new AbortController();
		abortRef.current = controller;

		setLoading( true );
		setProducts( [] );

		const query = buildQuery( { ...attributes, limit: effectiveLimit }, context );

		apiFetch( {
			path:   `/modern-woo/v1/products?${ query }`,
			signal: controller.signal,
		} )
			.then( ( data ) => {
				setProducts( data.products || [] );
				setLoading( false );
			} )
			.catch( ( err ) => {
				if ( err.name !== 'AbortError' ) {
					setLoading( false );
				}
			} );

		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		attributes.productSource,
		attributes.limit,
		attributes.productIds,
		attributes.sourceCategory,
		attributes.defaultOrderby,
		attributes.displayMode,
	] );

	if ( loading ) {
		return (
			<SkeletonLoader
				count={ effectiveLimit }
				columnsDesktop={ columnsDesktop }
				columnsTablet={ columnsTablet }
				columnsMobile={ columnsMobile }
				gap={ gap }
				gapTablet={ gap }
				gapMobile={ gap }
			/>
		);
	}

	if ( ! products.length ) {
		return (
			<p className="mwsb-showcase-empty">
				{ isEditor ? 'No products found — adjust the source settings.' : '' }
			</p>
		);
	}

	if ( displayMode === 'slider' ) {
		return (
			<SliderTrack
				attributes={ attributes }
				products={ products }
			/>
		);
	}

	return (
		<div
			className="mwsb-showcase-grid mwsb-grid"
			style={ {
				'--mwsb-cols-desktop': columnsDesktop,
				'--mwsb-cols-tablet':  columnsTablet,
				'--mwsb-cols-mobile':  columnsMobile,
				'--mwsb-gap':          `${ gap }px`,
				'--mwsb-gap-tablet':   `${ gap }px`,
				'--mwsb-gap-mobile':   `${ gap }px`,
			} }
		>
			{ products.map( ( product ) => (
				<ProductCard
					key={ product.id }
					product={ product }
					settings={ attributes }
				/>
			) ) }
		</div>
	);
}
